import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Database, ArrowRight } from "lucide-react";
import dayjs from "dayjs";

import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ModuleTitleCard, ContainerCard } from "../../components/ui/cards";
import { EMPLOYEE_MIGRATION_LIST } from "../../constants/api";

const STATUS_STYLES = {
    completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
};

const isAdmin = () =>
    JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
    JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

const EmployeeMigration = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const admin = useMemo(isAdmin, []);

    // Resume an existing batch: completed -> RM allocation, otherwise the
    // review grid. The wizard re-derives state from the id, so this is just a hint.
    const openBatch = (record) =>
        navigate(`/datamigration/employeemigration/batch/${record.id}`, {
            state: { status: record.status },
        });

    const columns = useMemo(
        () => [
            {
                accessor: "migration_date_time",
                Header: "Migrated Date & Time",
                sortable: true,
                Cell: ({ value }) =>
                    value ? dayjs(value).format("DD MMM YYYY • hh:mm A") : "-",
            },
            {
                accessor: "migrated_by",
                Header: "Migrated By",
                Cell: ({ value }) => value || "-",
            },
            {
                accessor: "count",
                Header: "Records",
                Cell: ({ value }) => `${value || 0}/100`,
            },
            {
                accessor: "status",
                Header: "Status",
                filterable: true,
                Cell: ({ value }) => {
                    const cls = STATUS_STYLES[value] ||
                        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
                    return (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
                            {value || "-"}
                        </span>
                    );
                },
            },
            {
                accessor: "actions",
                Header: "Action",
                Cell: ({ row }) => (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7"
                        onClick={() => openBatch(row)}
                    >
                        {row.status === "completed" ? "View" : "Resume"}
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    if (!admin) {
        return (
            <div>
                <ModuleTitleCard Icon={Database} title="Employee Migration" />
                <ContainerCard>
                    <p className="text-sm text-muted-foreground p-4">
                        You are not authorized to access employee data migration.
                    </p>
                </ContainerCard>
            </div>
        );
    }

    return (
        <div>
            <ModuleTitleCard
                Icon={Database}
                title="Employee Migration"
                subtitle="Bulk-import employee records from a spreadsheet, resolve conflicts, then migrate and assign reporting managers."
                totalRecords={totalRecords}
                buttonEnable
                buttonName="New Migration"
                setOpenDialog={() => navigate("/datamigration/employeemigration/new")}
            />

            <ContainerCard>
                <Reactable
                    pagename="employee-migration"
                    columns={columns}
                    data={data}
                    setData={setData}
                    setTotalCount={setTotalRecords}
                    url={EMPLOYEE_MIGRATION_LIST}
                    serverSide
                    tableHeight="calc(100vh - 320px)"
                    isCSVRequired
                />
            </ContainerCard>
        </div>
    );
};

export default EmployeeMigration;
